import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  Add as AddIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Reply as ReplyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import timezoneService from '../services/timezoneService';

interface TestComment {
  id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  feature: string;
  comment: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  replies?: TestCommentReply[];
}

interface TestCommentReply {
  id: string;
  comment_id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  reply: string;
  created_at: string;
  updated_at: string;
}

export const Testing: React.FC = () => {
  const { state: authState } = useAuth();
  const [comments, setComments] = useState<TestComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newComment, setNewComment] = useState({
    feature: '',
    comment: '',
    priority: 'medium' as TestComment['priority'],
  });
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<TestComment['status']>('open');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [replyInputs, setReplyInputs] = useState<{[commentId: string]: string}>({});
  const [showReplies, setShowReplies] = useState<{[commentId: string]: boolean}>({});
  const [submittingReply, setSubmittingReply] = useState<{[commentId: string]: boolean}>({});

  const features = [
    'Time Tracking',
    'Projects',
    'Team Management',
    'Reports',
    'AI Analytics',
    'Screenshots',
    'Chat',
    'Navigation',
    'Settings',
    'Authentication',
    'Other',
  ];

  // Load comments on component mount
  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTestingComments();
      setComments(data);
    } catch (error) {
      console.error('Error loading testing comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: TestComment['priority']) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: TestComment['status']) => {
    switch (status) {
      case 'open':
        return 'error';
      case 'in-progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleAddComment = async () => {
    if (!newComment.feature || !newComment.comment) {
      console.warn('❌ Cannot save comment - missing required fields:', { 
        feature: newComment.feature, 
        comment: newComment.comment 
      });
      return;
    }

    setSavingComment(true);
    try {
      console.log('🔄 Creating new testing comment:', newComment);
      console.log('🔄 Current user:', authState.user);
      
      const result = await apiService.createTestingComment({
        feature: newComment.feature,
        comment: newComment.comment,
        priority: newComment.priority,
      });
      
      console.log('✅ Comment created successfully:', result);

      // Reload comments to get the latest data
      await loadComments();
      console.log('✅ Comments reloaded successfully');

      setNewComment({
        feature: '',
        comment: '',
        priority: 'medium',
      });
      setShowAddDialog(false);
    } catch (error) {
      console.error('❌ Error creating testing comment:', error);
      // Show detailed error feedback to user
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to create comment: ${errorMessage}\n\nPlease check the browser console for more details.`);
    } finally {
      setSavingComment(false);
    }
  };

  const handleUpdateStatus = async (commentId: string, newStatus: TestComment['status']) => {
    setSavingStatus(true);
    try {
      console.log('🔄 Updating comment status:', { commentId, newStatus });
      console.log('🔄 Current user:', authState.user);
      console.log('🔄 User permissions:', authState.user?.permissions);
      console.log('🔄 User role:', authState.user?.role);
      
      const result = await apiService.updateTestingCommentStatus(commentId, newStatus);
      console.log('✅ Comment status updated successfully:', result);
      
      // Reload comments to get the latest data
      await loadComments();
      setEditingComment(null);
      
      // Show success feedback
      console.log('✅ Comments reloaded successfully');
    } catch (error) {
      console.error('❌ Error updating comment status:', error);
      // Show detailed error feedback to user
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to update comment status: ${errorMessage}\n\nPlease check the browser console for more details.`);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await apiService.deleteTestingComment(commentId);
      // Reload comments to get the latest data
      await loadComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleAddReply = async (commentId: string) => {
    const replyText = replyInputs[commentId];
    if (!replyText?.trim()) return;

    setSubmittingReply({ ...submittingReply, [commentId]: true });
    try {
      await apiService.createTestingCommentReply(commentId, replyText);
      // Clear the reply input
      setReplyInputs({ ...replyInputs, [commentId]: '' });
      // Reload comments to get updated replies
      await loadComments();
    } catch (error) {
      console.error('Error adding reply:', error);
    } finally {
      setSubmittingReply({ ...submittingReply, [commentId]: false });
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      await apiService.deleteTestingCommentReply(replyId);
      // Reload comments to get updated replies
      await loadComments();
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const toggleReplies = (commentId: string) => {
    setShowReplies({ ...showReplies, [commentId]: !showReplies[commentId] });
  };

  const updateReplyInput = (commentId: string, value: string) => {
    setReplyInputs({ ...replyInputs, [commentId]: value });
  };

  const startEditStatus = (commentId: string, currentStatus: TestComment['status']) => {
    setEditingComment(commentId);
    setEditStatus(currentStatus);
  };

  const cancelEdit = () => {
    setEditingComment(null);
    setEditStatus('open');
  };

  const saveStatusEdit = () => {
    if (editingComment) {
      handleUpdateStatus(editingComment, editStatus);
    }
  };

  // Check if user has admin permissions (MANAGE_TESTING)
  const hasManagePermission = authState.user?.permissions?.includes('manage_testing') || 
                              authState.user?.role === 'owner' || 
                              authState.user?.role === 'administrator';

  const filteredComments = comments.filter(comment => {
    const statusMatch = filterStatus === 'all' || comment.status === filterStatus;
    return statusMatch;
  });

  const stats = {
    total: comments.length,
    open: comments.filter(c => c.status === 'open').length,
    inProgress: comments.filter(c => c.status === 'in-progress').length,
    resolved: comments.filter(c => c.status === 'resolved').length,
    closed: comments.filter(c => c.status === 'closed').length,
    critical: comments.filter(c => c.priority === 'critical').length,
    high: comments.filter(c => c.priority === 'high').length,
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugReportIcon />
        Testing Dashboard
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        Welcome to the testing area! Here all testers can report bugs, suggest improvements, and share feedback about the app's features.
        All comments are visible to all testers so we can collaborate and help each other.
      </Typography>

      {/* Admin Information */}
      {hasManagePermission && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Comment Administration Guide:
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Edit Status:</strong> Click the edit icon (✏️) next to any comment to change its status (Open, In Progress, Resolved, Closed)
            <br />
            • <strong>Delete Comments:</strong> Click the delete icon (🗑️) to permanently remove inappropriate or duplicate comments
            <br />
            • <strong>Status Meanings:</strong> Open = New issue, In Progress = Being worked on, Resolved = Fixed/Completed, Closed = No action needed
            <br />
            • <strong>Best Practice:</strong> Update status regularly to keep testers informed about progress on their feedback
          </Typography>
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h4" color="primary">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Comments
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h4" color="error">
              {stats.open}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Open Issues
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h4" color="warning">
              {stats.inProgress}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h4" color="success">
              {stats.resolved}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Resolved
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h4" color="error">
              {stats.critical}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Critical Priority
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h4" color="warning">
              {stats.high}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              High Priority
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddDialog(true)}
          sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
        >
          Add Comment
        </Button>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in-progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={loadComments}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Comments List */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Test Comments & Feedback ({filteredComments.length})
          </Typography>

          {filteredComments.length === 0 ? (
            <Alert severity="info">
              No comments match the current filters. Try adjusting your filters or add a new comment.
            </Alert>
          ) : (
            <List>
              {filteredComments.map((comment, index) => (
                <React.Fragment key={comment.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                      <Avatar sx={{ mt: 1 }}>
                        {comment.user_name.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {comment.feature}
                          </Typography>
                          <Chip
                            label={`👤 ${comment.user_name}`}
                            size="small"
                            color="default"
                            variant="filled"
                            sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold' }}
                          />
                          <Chip
                            label={comment.priority}
                            size="small"
                            color={getPriorityColor(comment.priority) as any}
                          />
                          <Chip
                            label={comment.status}
                            size="small"
                            color={getStatusColor(comment.status) as any}
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Date:</strong> {timezoneService.formatUserDate(timezoneService.convertUTCToUserTime(comment.created_at))} | <strong>Updated:</strong> {timezoneService.formatUserDate(timezoneService.convertUTCToUserTime(comment.updated_at))}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {comment.comment}
                        </Typography>
                        
                        {/* Admin Controls */}
                        {hasManagePermission && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            {editingComment === comment.id ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={editStatus}
                                    onChange={(e) => setEditStatus(e.target.value as TestComment['status'])}
                                    size="small"
                                  >
                                    <MenuItem value="open">Open</MenuItem>
                                    <MenuItem value="in-progress">In Progress</MenuItem>
                                    <MenuItem value="resolved">Resolved</MenuItem>
                                    <MenuItem value="closed">Closed</MenuItem>
                                  </Select>
                                </FormControl>
                                <Button 
                                  size="small" 
                                  onClick={saveStatusEdit} 
                                  variant="contained" 
                                  color="primary"
                                  disabled={savingStatus}
                                  startIcon={savingStatus ? <CircularProgress size={16} /> : null}
                                >
                                  {savingStatus ? 'Saving...' : 'Save'}
                                </Button>
                                <Button size="small" onClick={cancelEdit} variant="outlined">
                                  Cancel
                                </Button>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit Status">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => startEditStatus(comment.id, comment.status)}
                                    color="primary"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Comment">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDeleteComment(comment.id)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Reply Section */}
                        <Box sx={{ mt: 2 }}>
                          {/* Toggle Replies Button */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => toggleReplies(comment.id)}
                              startIcon={showReplies[comment.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              sx={{ color: 'primary.main' }}
                            >
                              {comment.replies && comment.replies.length > 0 
                                ? `${comment.replies.length} ${comment.replies.length === 1 ? 'Reply' : 'Replies'}`
                                : 'Reply'}
                            </Button>
                          </Box>

                          {/* Reply Input */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                              size="small"
                              placeholder="Write a reply..."
                              value={replyInputs[comment.id] || ''}
                              onChange={(e) => updateReplyInput(comment.id, e.target.value)}
                              multiline
                              rows={2}
                              fullWidth
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleAddReply(comment.id);
                                }
                              }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleAddReply(comment.id)}
                              disabled={!replyInputs[comment.id]?.trim() || submittingReply[comment.id]}
                              startIcon={submittingReply[comment.id] ? <CircularProgress size={16} /> : <SendIcon />}
                              sx={{ 
                                minWidth: 'auto', 
                                px: 2, 
                                alignSelf: 'flex-start',
                                backgroundColor: '#10b981', 
                                '&:hover': { backgroundColor: '#059669' } 
                              }}
                            >
                              {submittingReply[comment.id] ? '' : 'Reply'}
                            </Button>
                          </Box>

                          {/* Display Replies */}
                          {showReplies[comment.id] && comment.replies && comment.replies.length > 0 && (
                            <Box sx={{ pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                              {comment.replies.map((reply) => (
                                <Box key={reply.id} sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                    {reply.user_name.charAt(0)}
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Typography variant="body2" fontWeight="bold" color="primary">
                                        {reply.user_name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {timezoneService.formatUserDate(timezoneService.convertUTCToUserTime(reply.created_at))}
                                      </Typography>
                                      {/* Delete reply button for admins or reply author */}
                                      {(hasManagePermission || reply.user_id === authState.user?.id) && (
                                        <Tooltip title="Delete Reply">
                                          <IconButton 
                                            size="small" 
                                            onClick={() => handleDeleteReply(reply.id)}
                                            sx={{ ml: 'auto', color: 'error.main' }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    <Typography variant="body2">
                                      {reply.reply}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < filteredComments.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add Comment Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Test Comment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Feature</InputLabel>
              <Select
                value={newComment.feature}
                onChange={(e) => setNewComment({ ...newComment, feature: e.target.value })}
                label="Feature"
              >
                {features.map((feature) => (
                  <MenuItem key={feature} value={feature}>
                    {feature}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newComment.priority}
                onChange={(e) => setNewComment({ ...newComment, priority: e.target.value as TestComment['priority'] })}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comment"
              value={newComment.comment}
              onChange={(e) => setNewComment({ ...newComment, comment: e.target.value })}
              placeholder="Detailed description of what you found, steps to reproduce, or your suggestion..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)} disabled={savingComment}>Cancel</Button>
          <Button
            onClick={handleAddComment}
            variant="contained"
            disabled={!newComment.feature || !newComment.comment || savingComment}
            startIcon={savingComment ? <CircularProgress size={16} /> : null}
          >
            {savingComment ? 'Saving...' : 'Add Comment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
